import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Film, Eye, Share2, MousePointerClick, Trophy, Flag, Target,
  Gift, Users, MessageCircle, Music2, Instagram, Sparkles,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { getCreatorStudioSnapshot } from "@/lib/creatorStudio.functions";
import { getTopCreators } from "@/lib/worldPulseLive.functions";
import { ExportClipButton } from "@/components/clip/ExportClipButton";
import { AmbassadorMissions } from "@/components/ambassadors/AmbassadorMissions";
import { MyRewardsCenter } from "@/components/rewards/MyRewardsCenter";
import { CreatorLinkBuilder } from "@/components/ambassadors/CreatorLinkBuilder";
import { NationGate } from "@/components/onboarding/NationGate";
import { ActivationBanner } from "@/components/onboarding/ActivationBanner";

export const Route = createFileRoute("/creator-studio")({
  head: () => ({
    meta: [
      { title: "Creator Studio — VerseSport" },
      { name: "description", content: "Tu centro de mando creativo: clips virales, ranking mundial, misiones, recompensas y exportaciones a WhatsApp, TikTok e Instagram." },
    ],
  }),
  component: CreatorStudioPage,
});

function CreatorStudioPage() {
  const { isAuthenticated, user } = useAuth();
  const fetchSnapshot = useServerFn(getCreatorStudioSnapshot);
  const [rewardsOpen, setRewardsOpen] = useState(false);

  // Phase 4 — pass the creator's referral code so the server can attribute
  // audit_master events (shares/clicks/views) tagged with ref_code back to
  // this user. Code lives in localStorage (set by getOrCreateReferralCode).
  const refCode =
    typeof window !== "undefined" ? window.localStorage.getItem("ss2026_ref_code") : null;

  const snap = useQuery({
    queryKey: ["creatorStudio", user?.id ?? "anon", refCode ?? ""],
    queryFn: () => fetchSnapshot({ data: { ref_code: refCode } }),
    enabled: isAuthenticated,
    refetchInterval: 30_000,
  });
  const topQ = useQuery({
    queryKey: ["worldPulse", "top_creators"],
    queryFn: () => getTopCreators(),
    refetchInterval: 30_000,
  });

  const totals = snap.data?.totals ?? { clips: 0, views: 0, shares: 0, clicks: 0 };
  const rank = snap.data?.rank ?? { position: null, total: 0, score: 0 };
  const recent = snap.data?.recent ?? [];
  const byPlatform = snap.data?.byPlatform ?? [];
  const byNation = snap.data?.byNation ?? [];
  const handle = user?.email?.split("@")[0] ?? "creator";
  // Single source of truth for nation — same field Nation Wars reads (profiles.selection_team).
  const nation = snap.data?.nation ?? "—";

  return (
    <main className="mx-auto max-w-5xl space-y-5 px-4 py-6 text-white">
      <NationGate surface="creator_studio" />
      <header className="flex items-center justify-end gap-3">
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Creator Studio</div>
          <h1 className="text-2xl font-black tracking-tight">@{handle}</h1>
        </div>
      </header>
      <ActivationBanner surface="creator_studio" />

      {/* SECCIÓN 1 — CREATOR COMMAND CENTER */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-black uppercase tracking-wider">
          <Users className="h-4 w-4 text-sky-400" /> Mis Referidos
        </h2>
        <p className="mb-3 text-[11px] text-white/50">
          Genera tu enlace oficial y recluta fans para tu nación.
        </p>
        <CreatorLinkBuilder />
      </section>

      {/* SECCIÓN 2 — CENTRO DE EXPORTACIÓN */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wider">
          <Share2 className="h-4 w-4 text-primary" /> Centro de Exportación
        </h2>
        <div className="grid gap-3 md:grid-cols-3">
          <ExportTile
            icon={<MessageCircle className="h-4 w-4 text-emerald-400" />}
            title="WhatsApp"
            desc="Reta a tus amigos con tu clip"
            payload={{ kind: "mi_clip_viral", headline: "Esto rompió el Mundial" }}
          />
          <ExportTile
            icon={<Music2 className="h-4 w-4 text-fuchsia-400" />}
            title="TikTok"
            desc="Formato 9:16 listo para subir"
            payload={{ kind: "batalla_naciones", headline: "Mi nación va por el #1" }}
          />
          <ExportTile
            icon={<Instagram className="h-4 w-4 text-pink-400" />}
            title="Instagram"
            desc="Story / Reel optimizado"
            payload={{ kind: "mi_nacion", headline: "Mi nación no se rinde" }}
          />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <ExportClipButton clip={{ kind: "mi_nacion" }} label="🇲🇽 Mi Nación" />
          <ExportClipButton clip={{ kind: "mi_ranking", position: rank.position ?? 1, totalCompetitors: rank.total }} label="🏆 Mi Ranking" />
          <ExportClipButton clip={{ kind: "batalla_naciones", rivalNation: "BRA" }} label="⚔️ Batalla" />
          <ExportClipButton clip={{ kind: "nuevo_badge", reward: "Creator Pro" }} label="🎖️ Badge" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Link
            to="/media-blitz"
            className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-cyan-200 transition hover:bg-cyan-400/20"
          >
            <Sparkles className="h-3.5 w-3.5" /> Media Blitz · Clips Virales
          </Link>
          <Link
            to="/viral-cards"
            className="inline-flex items-center gap-1.5 rounded-full border border-pink-400/40 bg-pink-400/10 px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-pink-200 transition hover:bg-pink-400/20"
          >
            <Share2 className="h-3.5 w-3.5" /> Viral Cards
          </Link>
        </div>
      </section>

      {/* SECCIÓN 3 — AMBASSADOR MISSIONS */}
      <AmbassadorMissions />

      {/* SECCIÓN 4 — MIS RECOMPENSAS */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wider">
          <Gift className="h-4 w-4 text-emerald-400" /> Mis Recompensas
        </h2>
        <button
          onClick={() => setRewardsOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-black text-emerald-950 active:scale-95"
        >
          <Gift className="h-3.5 w-3.5" /> Abrir mis recompensas
        </button>
        <MyRewardsCenter open={rewardsOpen} onClose={() => setRewardsOpen(false)} />
      </section>

      {/* SECCIÓN 5 — ESTADÍSTICAS PERSONALES */}
      {/* Hero — identidad + ranking */}
      <section className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[oklch(0.12_0.05_280)] via-[oklch(0.09_0.04_300)] to-[oklch(0.08_0.05_15)] p-5 shadow-[0_30px_80px_-30px_rgba(0,0,0,.7)]">
        <div className="pointer-events-none absolute inset-0 -z-0 bg-[radial-gradient(circle_at_30%_20%,rgba(244,63,94,.25),transparent_60%),radial-gradient(circle_at_80%_70%,rgba(99,102,241,.25),transparent_60%)]" />
        <div className="relative grid gap-4 md:grid-cols-3">
          <Stat icon={<Trophy className="h-4 w-4" />} label="Mi Ranking Mundial" value={rank.position ? `#${rank.position}` : "—"} sub={rank.total ? `de ${rank.total} creadores 24h` : "Sin ranking aún"} />
          <Stat icon={<Flag className="h-4 w-4" />} label="Mi Nación" value={nation} sub="Defendiendo tu bandera" />
          <Stat icon={<Sparkles className="h-4 w-4" />} label="Score viral" value={rank.score.toLocaleString()} sub="shares×3 · clicks×2 · views" />
        </div>
      </section>

      {/* KPIs */}
      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi icon={<Film className="h-3.5 w-3.5" />} label="Mis Clips Virales" value={totals.clips} accent="from-rose-500/30" />
        <Kpi icon={<Share2 className="h-3.5 w-3.5" />} label="Mis Shares" value={totals.shares} accent="from-emerald-500/30" />
        <Kpi icon={<Eye className="h-3.5 w-3.5" />} label="Mis Vistas" value={totals.views} accent="from-indigo-500/30" />
        <Kpi icon={<MousePointerClick className="h-3.5 w-3.5" />} label="Mis Clicks" value={totals.clicks} accent="from-amber-500/30" />
      </section>

      {!isAuthenticated && (
        <div className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4 text-sm">
          Inicia sesión para registrar tus clips, subir en el ranking mundial y desbloquear recompensas.{" "}
          <Link to="/login" search={{ from: undefined }} className="font-bold underline">Entrar</Link>
        </div>
      )}

      {/* SECCIÓN 6 — ANALÍTICAS E HISTORIAL */}
      {/* Mis Clips Virales */}
      <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wider">
          <Film className="h-4 w-4 text-rose-400" /> Mis Clips Virales
        </h2>
        {snap.isLoading ? (
          <SkeletonList />
        ) : recent.length === 0 ? (
          <Empty label="Aún no has creado ningún clip. Pulsa cualquier botón del Centro de Exportación para empezar." />
        ) : (
          <ul className="divide-y divide-white/5">
            {recent.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="line-clamp-1 text-sm font-black">
                    {c.kind.toUpperCase()}
                    {c.nation && <span className="ml-2 text-white/60">· {c.nation}</span>}
                    {c.rival && <span className="text-white/40"> vs {c.rival}</span>}
                  </div>
                  <div className="line-clamp-1 text-[11px] text-white/50">
                    {c.headline ?? ""} {c.platform ? ` · ${c.platform}` : ""} · {new Date(c.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[11px] tabular-nums text-white/70">
                  <span title="views" className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{c.views}</span>
                  <span title="shares" className="inline-flex items-center gap-1"><Share2 className="h-3 w-3" />{c.shares}</span>
                  <span title="clicks" className="inline-flex items-center gap-1"><MousePointerClick className="h-3 w-3" />{c.clicks}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Top Creadores Mundial */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wider">
            <Trophy className="h-4 w-4 text-amber-400" /> Top Creadores Mundial
          </h2>
          {topQ.isLoading ? <SkeletonList /> : (topQ.data?.rows.length ?? 0) === 0 ? (
            <Empty label="Aún no hay creadores en racha. Sé el primero." />
          ) : (
            <ol className="space-y-1.5">
              {topQ.data!.rows.map((c, i) => (
                <li key={c.user_id} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-white/5">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="w-5 text-right text-xs font-black text-white/40">{i + 1}</span>
                    {c.avatar_url ? (
                      <img src={c.avatar_url} alt="" className="h-7 w-7 rounded-full border border-white/15 object-cover" />
                    ) : (
                      <div className="h-7 w-7 rounded-full bg-white/10" />
                    )}
                    <div className="truncate text-sm font-bold">{c.display_name ?? "Fan"}</div>
                  </div>
                  <div className="text-right text-[11px] tabular-nums">
                    <div className="font-black">{c.score}</div>
                    <div className="text-white/40">{c.clips} clips · {c.shares}↗</div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* Plataformas + Naciones */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wider">
            <Target className="h-4 w-4 text-indigo-400" /> Tu impacto por canal
          </h2>
          {byPlatform.length === 0 ? (
            <Empty label="Aparecerá cuando exportes a WhatsApp, TikTok o Instagram." />
          ) : (
            <ul className="space-y-1.5">
              {byPlatform.map((p) => (
                <li key={p.platform} className="flex items-center justify-between text-xs">
                  <span className="font-bold uppercase tracking-wider text-white/70">{p.platform}</span>
                  <span className="tabular-nums text-white/60">{p.clips} clips · {p.shares}↗</span>
                </li>
              ))}
            </ul>
          )}
          <h3 className="mt-4 mb-2 text-[11px] font-black uppercase tracking-wider text-white/50">Naciones que has defendido</h3>
          {byNation.length === 0 ? (
            <div className="text-[11px] text-white/40">Crea tu primer clip para empezar.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {byNation.map((n) => (
                <span key={n.nation} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] font-bold">
                  {n.nation} · {n.clips}
                </span>
              ))}
            </div>
          )}
        </section>
      </div>
    </main>
  );
}

function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-3 backdrop-blur">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/60">
        {icon} {label}
      </div>
      <div className="mt-1 text-2xl font-black tracking-tight">{value}</div>
      {sub && <div className="text-[11px] text-white/40">{sub}</div>}
    </div>
  );
}

function Kpi({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: number; accent: string }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br ${accent} via-transparent to-transparent p-3`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/70">
        {icon} {label}
      </div>
      <div className="mt-1 text-2xl font-black tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

function ExportTile({
  icon, title, desc, payload,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  payload: Parameters<typeof ExportClipButton>[0]["clip"];
}) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-white/10 bg-white/[0.04] p-3">
      <div className="flex items-center gap-2 text-xs font-black">{icon}{title}</div>
      <div className="text-[11px] text-white/50">{desc}</div>
      <ExportClipButton clip={payload} label={`Exportar a ${title}`} />
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-4 text-xs text-white/40">{label}</div>;
}

function SkeletonList() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-9 animate-pulse rounded-lg bg-white/[0.04]" />
      ))}
    </div>
  );
}
